import React from 'react';
import { formatDeviceTypeLabel } from '../../pages/admin/devices/deviceTypes';

export type FieldDeviceLineResult = {
    itemId: string;
    label?: string;
    result?: string | null;
    note?: string;
};

export type FieldDeviceRecordDetailData = {
    deviceLabel: string;
    location: string;
    overallResult: string;
    note?: string;
    completedAt?: string;
    lineResults?: FieldDeviceLineResult[];
};

type Props = {
    record: FieldDeviceRecordDetailData;
    onClose: () => void;
};

function resultLabel(result?: string | null): string {
    if (!result) return '—';
    if (result === 'n/a') return 'N/A';
    if (result === 'pass') return 'Pass';
    if (result === 'fail' || result === 'deficient') return 'Fail';
    return result;
}

function resultTone(result?: string | null): string {
    if (result === 'pass') return 'pass';
    if (result === 'fail' || result === 'deficient') return 'fail';
    if (result === 'n/a') return 'na';
    return 'pending';
}

export default function FieldDeviceRecordDetail({ record, onClose }: Props): React.JSX.Element {
    const lines = record.lineResults || [];
    const overallTone = record.overallResult === 'n/a' ? 'na' : record.overallResult;

    return (
        <div className="ir-field-detail" role="region" aria-label="Device inspection summary">
            <div className="ir-field-detail__head">
                <div>
                    <h4 className="ir-field-detail__title">{record.deviceLabel}</h4>
                    <p className="ir-field-detail__meta">{record.location}</p>
                </div>
                <button type="button" className="ir-field-detail__close" onClick={onClose} aria-label="Close summary">
                    Close
                </button>
            </div>

            <div className="ir-field-detail__chips">
                <span className={`ir-field-detail__chip ir-field-detail__chip--${overallTone}`}>
                    Overall: {resultLabel(record.overallResult)}
                </span>
                {record.completedAt ? (
                    <span className="ir-field-detail__chip ir-field-detail__chip--muted">
                        {new Date(record.completedAt).toLocaleString()}
                    </span>
                ) : null}
            </div>

            {record.note?.trim() ? (
                <p className="ir-field-detail__note">
                    <strong>Note:</strong> {record.note.trim()}
                </p>
            ) : null}

            <div className="ir-field-detail__list">
                {lines.length === 0 ? (
                    <p className="ir-field-detail__empty">No checklist lines recorded for this device.</p>
                ) : (
                    lines.map((line) => {
                        const tone = resultTone(line.result);
                        return (
                            <div key={line.itemId} className={`ir-field-detail__row ir-field-detail__row--${tone}`}>
                                <span className="ir-field-detail__label">{line.label || line.itemId}</span>
                                <span className={`ir-field-detail__result ir-field-detail__result--${tone}`}>
                                    {resultLabel(line.result)}
                                </span>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export function recordDetailFromLog(record: {
    deviceSnapshot?: { type?: string; installationLocation?: string; floor?: string; zone?: string };
    deviceId?: string | { type?: string; installationLocation?: string; floor?: string; zone?: string } | null;
    overallResult: string;
    note?: string;
    completedAt?: string;
    lineResults?: FieldDeviceLineResult[];
}): FieldDeviceRecordDetailData {
    const snap = record.deviceSnapshot;
    const dev = typeof record.deviceId === 'object' ? record.deviceId : null;
    const loc = [snap?.installationLocation || dev?.installationLocation, snap?.floor, snap?.zone]
        .filter(Boolean)
        .join(' · ') || '—';
    return {
        deviceLabel: formatDeviceTypeLabel(snap?.type || dev?.type),
        location: loc,
        overallResult: record.overallResult,
        note: record.note,
        completedAt: record.completedAt,
        lineResults: record.lineResults
    };
}
